import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Confirm, Loading } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, rupiah } from "@/lib/format";

const DaysLeft = ({ d }) => <span className={`font-semibold ${d <= 7 ? "text-rose-600" : "text-slate-700"}`} data-testid="trash-days-left">{d} hari</span>;

function Actions({ t, i, onRestore, onPurge, big }) {
  const { can } = useAuth();
  const cls = big ? "h-11 flex-1" : "h-8";
  const sfx = big ? `card-${i}` : `${i}`;
  return (
    <div className="flex gap-2">
      {can("customer:restore") && <Button size="sm" onClick={() => onRestore(t)} className={`${cls} bg-emerald-700 hover:bg-emerald-800`} data-testid={`trash-restore-${sfx}`}><RotateCcw className="h-4 w-4 mr-1" />Pulihkan</Button>}
      {can("customer:purge") && <Confirm title={`Hapus permanen ${t.name}?`} description="Data pelanggan, rekap, hutang, dan riwayat pembayarannya tidak bisa dipulihkan lagi." onConfirm={() => onPurge(t)}
        trigger={<Button size="sm" className={`${cls} bg-rose-600 hover:bg-rose-700`} data-testid={`trash-purge-${sfx}`}><Trash2 className="h-4 w-4 mr-1" />Hapus Permanen</Button>} />}
    </div>
  );
}

function TrashCard({ t, i, ...h }) {
  return (
    <div className="border border-slate-200 bg-slate-50 rounded-sm p-3 space-y-2" data-testid={`trash-card-${i}`}>
      <div className="flex justify-between gap-2"><div className="font-medium">{t.name}</div><DaysLeft d={t.days_left} /></div>
      <div className="text-xs text-slate-600">{t.address ? `${t.address} · ` : ""}Sisa hutang <b>{rupiah(t.debt_remaining)}</b></div>
      <div className="text-xs text-slate-500">Dihapus {t.deleted_by} · {fmtDateTime(t.deleted_at)} — {t.reason}</div>
      <Actions t={t} i={i} big {...h} />
    </div>
  );
}

export function TrashList() {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try { setRows((await api.get("/customers/trash")).data); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const onRestore = async (t) => {
    try {
      const { data } = await api.post(`/customers/trash/${t.id}/restore`);
      toast.success(`${data.name} dipulihkan beserta rekap & hutangnya`);
      if (data.duplicate_name) toast.warning(`Sudah ada pelanggan lain bernama "${data.name}" — periksa nama ganda`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onPurge = async (t) => {
    try { await api.delete(`/customers/trash/${t.id}`); toast.success(`${t.name} dihapus permanen`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows) return <Loading />;
  const h = { onRestore, onPurge };
  return (
    <div className="space-y-3" data-testid="trash-list">
      <p className="text-xs text-slate-500">Pelanggan yang dihapus disimpan 30 hari, lalu terhapus permanen otomatis. Invoice yang dibatalkan saat penghapusan tetap DIBATALKAN setelah dipulihkan.</p>
      {rows.length === 0 && <div className="text-center text-slate-500 py-8 border border-dashed rounded-sm" data-testid="trash-empty">Tempat Sampah kosong</div>}
      <div className="md:hidden space-y-2">{rows.map((t, i) => <TrashCard key={t.id} t={t} i={i} {...h} />)}</div>
      {rows.length > 0 && (
        <div className="xl-wrap hidden md:block">
          <table className="xl">
            <thead><tr className="[&>th]:bg-slate-100"><th className="rownum">#</th><th>Nama</th><th>Alamat</th><th className="text-right">Sisa Hutang</th><th>Dihapus oleh</th><th>Waktu</th><th>Alasan</th><th>Sisa Hari</th><th>Aksi</th></tr></thead>
            <tbody>
              {rows.map((t, i) => (
                <tr key={t.id} className="bg-slate-50" data-testid={`trash-row-${i}`}>
                  <td className="rownum">{i + 1}</td><td className="font-medium">{t.name}</td><td className="text-slate-600">{t.address}</td>
                  <td className="num">{rupiah(t.debt_remaining)}</td><td>{t.deleted_by}</td><td className="text-xs">{fmtDateTime(t.deleted_at)}</td>
                  <td className="text-xs max-w-[220px] truncate" title={t.reason}>{t.reason}</td><td><DaysLeft d={t.days_left} /></td>
                  <td><Actions t={t} i={i} {...h} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
